import React, { useEffect, useMemo, useState } from 'react';
// import ConfirmationPopup from '../../Modals/ConfirmationPopup';
import { Box, Grid2, Chip, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styles from '../../StockTransfer.module.css';
import { useCreateReceive, useReceiveProspectiveList } from '../../../Queries/UseStockTransferQueries';
import { useLoader } from '../../../../../components/loaderProvider/loader';
import { useStockTransferStore } from '../../../Store/useStockTransferstore';
import ReceiveProspectiveListTable from '../ReceiveProspectiveListTable';
import { Column, IndentDetail } from '../../Issue/Issue.helper';
import { ReceiveProspectiveListResponse } from '../Receive.helper';
import { convertDateToDDMMYYYY, convertUnderscoreToTitle } from '../../../../../utils/commonUtils';
import SelectReasonPoup from '../../Modals/SelectReasonPoup';
import useCurrency from '../../../../../components/CurrencyProvider';
import { useAlert } from '../../../../../components/alertProvider';
import { useQueryClient } from '@tanstack/react-query';
import { getButtonStates } from '../../../../../utils/permissionService';
import { useClinic } from '../../../../../components/ClinicProvider';

type ReceivedProspectiveRow = {
  id: string | number;
  patient: string;
  indentNoDate: string;
  issueNoDate: string | Date;
  fromClinicStore: string;
  toClinicStore: string;
  totalAmount?: string;
  status?: React.ReactNode;
  issueAgainst?: React.ReactNode;
  issueType?: React.ReactNode;
  indent_details?: IndentDetail[];
};

const ProspectiveListTabComponent = () => {
  const { t: tStockTransfer } = useTranslation('stockTransfer_config');
  const { currencySymbol } = useCurrency();
  const { canEdit, canAdd } = getButtonStates('Admin', 'Stock Transfer', 'Receive');
  
  const BaseProspectiveHeaders: Column<ReceivedProspectiveRow>[] = [
    { label: tStockTransfer('Issue No | Date'), field: 'issueNoDate' },
    { label: tStockTransfer('Patient'), field: 'patient' },
    { label: tStockTransfer('Clinic | Receiving Store'), field: 'fromClinicStore' },
    { label: tStockTransfer('Clinic | Issued Store'), field: 'toClinicStore' },
    { label: `${tStockTransfer('Total Amount')} (${currencySymbol})`, field: 'totalAmount' },
    { label: tStockTransfer('Issue Against'), field: 'issueAgainst' },
    { label: tStockTransfer('Issue Type'), field: 'issueType' },
  ];
  
  const stockTransferStore = useStockTransferStore();
  const { setProspectiveTotalCount } = useStockTransferStore();
  const [selectedTab, setSelectedTab] = useState('ALL');
  const { showLoader, hideLoader } = useLoader();
  const [debouncedSearchText, setDebouncedSearchText] = useState(stockTransferStore.searchText);
  const [prospectiveTableData, setProspectiveTableData] = useState<ReceivedProspectiveRow[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<Array<string | number>>([]);
  const [isReasonPopupOpen, setIsReasonPopupOpen] = useState(false);
  const [reasonText, setReasonText] = useState('');
  const { mutateAsync: createReceive } = useCreateReceive();
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const { addAlert } = useAlert();
  const queryClient = useQueryClient();
  const { ClinicId } = useClinic();

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(stockTransferStore.searchText);
    }, 600);
    return () => {
      clearTimeout(handler);
    };
  }, [stockTransferStore.searchText]);
  
  const getIssueTypeChip = (issueType: string, issueTypeLabel: string) => {
    let color = '#757575';
    let bg = '#F5F5F5';
     
    switch (issueType.toUpperCase()) {
    case 'DIRECT':
      color = '#3A80E2';
      bg = '#E3F2FD';
      break;
    case 'INDENT':
      color = '#9045DB';
      bg = '#F3E5F5';
      break;
    case 'PR':
      color = '#E1CF32';
      bg = '#FFF3E0';
      break;
    }
     
    return (
      <Chip
        label={tStockTransfer(convertUnderscoreToTitle(issueTypeLabel))}
        size="small"
        data-testid={`receive-prospective-issue-type-chip-${issueType.toLowerCase()}`}
        sx={{
          color,
          backgroundColor: bg,
          fontWeight: 600,
          borderRadius: '18px',
          px: 1.5,
        }}
      />
    );
  };

  
  const getIssueAgainstChip = (isPatient: boolean) => {
    const label = isPatient ? tStockTransfer('Patient') : tStockTransfer('Store');
    const color = isPatient ? '#FB8C00' : '#2E7D32';
    const bg = isPatient ? '#FFF3E0' : '#E8F5E9';
    return (
      <Chip
        label={label}
        size="small"
        data-testid={`receive-prospective-issue-against-chip-${isPatient ? 'patient' : 'store'}`}
        sx={{
          color,
          backgroundColor: bg,
          fontWeight: 600,
          borderRadius: '18px',
          // border: `1px solid ${color}`,
          px: 1.5,
        }}
      />
    );
  };
     
  const ReceiveListParams = useMemo(() => ({
    limit: rowsPerPage,
    offset: page * rowsPerPage,
    receive_status: selectedTab !== 'ALL' ? selectedTab : undefined,
    search: debouncedSearchText ? debouncedSearchText : undefined,
    // Only apply filter values when Apply is clicked (trigger changes)
    created_on_after: stockTransferStore.fromDate ? stockTransferStore.fromDate : undefined,
    created_on_before: stockTransferStore.toDate ? stockTransferStore.toDate : undefined,
    from_store: stockTransferStore.fromStoreId ? stockTransferStore.fromStoreId : undefined,
    to_store: stockTransferStore.toStoreId ? stockTransferStore.toStoreId : undefined,
    receiving_clinic: ClinicId ? ClinicId : undefined,
    // eslint-disable-next-line
  }), [rowsPerPage, page, selectedTab, stockTransferStore.applyFiltersTrigger, debouncedSearchText, ClinicId]);

  const { data: ReceiveProspectiveList, isLoading, isFetching, refetch } = useReceiveProspectiveList(ReceiveListParams);


  useEffect(() => {
    if (ReceiveProspectiveList && Array.isArray(ReceiveProspectiveList.objects)) {
      // Update total count in store
      const count = ReceiveProspectiveList?.meta?.total_count ?? 0;
      setProspectiveTotalCount(Number(count) || 0);

      const mapped: ReceivedProspectiveRow[] = (ReceiveProspectiveList.objects).map((item: ReceiveProspectiveListResponse) => ({
        id: item?.id,
        patient: item?.patient ? `${item?.patient?.first_name} ${item?.patient?.last_name}` : '-',
        indentNoDate: `${item?.indent_no ?? '-'} | ${item?.indent_date ? convertDateToDDMMYYYY(item?.indent_date) : '-'}`,
        prNoDate: item?.pr_no ?? '-',
        issueNoDate: `${item?.issue_no ?? '-'} | ${item?.issue_date ? convertDateToDDMMYYYY(item?.issue_date) : '-'}`,
        fromClinicStore: `${item?.to_clinic_label ??  ''} | ${item?.to_store_label ?? item?.to_store ?? ''}`.trim(),
        toClinicStore: `${item?.clinic_label ?? item?.clinic ?? ''} | ${item?.from_store_label ?? item?.from_store ?? ''}`.trim(),
        totalAmount: item?.total_amount ?? '-',
        issueType: getIssueTypeChip(item?.issue_type ?? '-', item?.issue_type_label ?? item?.issue_type ?? '-'),
        issueAgainst: getIssueAgainstChip(Boolean(item?.patient)),
        indent_details: item?.issue_item_details?.map((d) => ({
          ...d,
          Taxes: d.taxes
        })) as unknown as IndentDetail[],
        issueRemark: item?.remark ?? '-',
      }));
      setProspectiveTableData(mapped);
    } else {
      setProspectiveTotalCount(0);
      setProspectiveTableData([]);
    }
    // eslint-disable-next-line
  }, [ReceiveProspectiveList, setProspectiveTotalCount]);


  const DisplayActionsButton: Record<string, React.ReactNode[]> = {

    DIRECT: (canEdit || canAdd) ? [
      <Button key="forward" variant="contained" className={styles.footerButton} disabled={selectedRowIds.length !== 1} onClick={() => setIsReasonPopupOpen(true)} data-testid="receive-prospective-direct-receive-btn">{tStockTransfer('Receive')}</Button>,
    ] : [],
    PR: (canEdit || canAdd) ? [
      <Button key="PR" variant="contained" className={styles.footerButton} disabled={selectedRowIds.length !== 1} onClick={() => setIsReasonPopupOpen(true)} data-testid="receive-prospective-pr-receive-btn">{tStockTransfer('Receive')}</Button>,
    ] : [],
    ISSUED: (canEdit || canAdd) ? [
      <Button key="issue" variant="contained" className={styles.footerButton} disabled={selectedRowIds.length !== 1} onClick={() => setIsReasonPopupOpen(true)} data-testid="receive-prospective-issued-receive-btn">{tStockTransfer('Receive')}</Button>,
    ] : [],
  };


  // Clear checkbox selection when filter tab changes
  useEffect(() => {
    setSelectedRowIds([]);
    stockTransferStore.clearSelectedReceiveItems();
    // eslint-disable-next-line
  }, [selectedTab]);

  const handleSaveReceiveRemark = async () => {
    try {
      const ids = selectedRowIds.map(Number).filter((n) => !Number.isNaN(n));
      showLoader();
      const itemsPayload = stockTransferStore.selectedReceiveItems.map((item) => ({
        issue_detail_id: item.issue_detail_id,
        received_qty: item.received_qty ?? 0,
        base_mrp: item.base_mrp,
        base_cost_price: item.base_cost_price,
        total_base_cost_price: item.total_base_cost_price,
        total_mrp_cost_price: item.total_mrp_cost_price,
        cgst_percent: item.cgst_percent,
        cgst_amount: item.cgst_amount,
        sgst_percent: item.sgst_percent,
        sgst_amount: item.sgst_amount,

      }));
      for (const id of ids) {
        await createReceive({ id, data: { items: itemsPayload, remark: reasonText } });
      }
      addAlert(tStockTransfer('Receive Created Successfully.'), 'success');
      setIsReasonPopupOpen(false);
      setReasonText('');
      stockTransferStore.clearSelectedReceiveItems();
      setSelectedRowIds([]);
      await refetch();
      // Invalidate all related queries to ensure data is refreshed across tabs
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['availableStockList'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['IndentList'], refetchType: 'all' }),
      ]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      addAlert(err?.response?.message || err?.message || tStockTransfer('Failed to create receive. Please retry.'), 'error');
    } finally {
      hideLoader();
    }
  };


  const ProspectiveFilterTabs = [
    { key: 'ALL', label: tStockTransfer('All'), count: ReceiveProspectiveList?.status_counts?.ALL ?? 0 },
    { key: 'DIRECT', label: tStockTransfer('Direct'), count: ReceiveProspectiveList?.status_counts?.DIRECT ?? 0 },
    { key : 'ISSUED', label: tStockTransfer('Indent'), count: ReceiveProspectiveList?.status_counts?.ISSUED ?? 0 },
    { key: 'PR', label: tStockTransfer('PR'), count: ReceiveProspectiveList?.status_counts?.PR ?? 0 },
  ];

  return(
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Grid2 container alignItems="left" justifyContent="space-between" width="100%"  my={1} data-testid="receive-prospective-title-container">
        <Typography variant="h6" fontWeight={700} data-testid="receive-prospective-title">
          {tStockTransfer('Prospective Items List')}
        </Typography>
        
      </Grid2>
      <Grid2 py={2} data-testid="receive-prospective-filter-tabs-container">
        <Box display="flex" gap={1.5} flexWrap="wrap">
          {ProspectiveFilterTabs.map((t) => {
            const isActive = selectedTab === t.key;
            return (
              <Box
                key={t.key}
                onClick={() => { setSelectedTab(t.key); }}
                data-testid={`receive-prospective-filter-tab-${t.key.toLowerCase()}`}
                className={`${styles.filterTab} ${isActive ? styles.active : styles.inactive}`}
              >
                {`${t.label} (${t.count})`}
              </Box>
            );
          })}
        </Box>
      </Grid2>

      <Grid2 sx={{ flex: 1, minHeight: 0, overflow: 'auto' }} data-testid="receive-prospective-table-wrapper">
        <ReceiveProspectiveListTable
          key={selectedTab}
          columns={BaseProspectiveHeaders}
          data={prospectiveTableData}
          selectedKeys={selectedRowIds.map(String)}
          onSelectionChange={(ids) => setSelectedRowIds(ids)}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={ReceiveProspectiveList?.meta?.total_count ?? prospectiveTableData.length}
          onPageChange={setPage}
          showCheckboxes={selectedTab !== 'ALL' && (canEdit || canAdd)}
          isLoading={isLoading || isFetching}
        />
      </Grid2>

      {/* Sticky Footer for Action Buttons */}
      {DisplayActionsButton[selectedTab] && DisplayActionsButton[selectedTab].length > 0 && (
        <Box 
          data-testid="receive-prospective-actions-container"
          sx={{ 
            flexShrink: 0,
            backgroundColor: '#fff', 
            borderTop: '1px solid #E2E3E5',
            py: 1.5,
            display: 'flex', 
            justifyContent: 'flex-end', 
            alignItems: 'center', 
            gap: 2
          }}
        >
          {DisplayActionsButton[selectedTab]}
        </Box>
      )} 

      <SelectReasonPoup
        open={isReasonPopupOpen}
        title={tStockTransfer('Receive Item')}
        label={tStockTransfer('Receive Remark')}
        isFromReceive={true}
        value={reasonText}
        onChange={setReasonText}
        onClose={() => setIsReasonPopupOpen(false)}
        onSave={handleSaveReceiveRemark}
      />
    </Box>
  );

};
export default ProspectiveListTabComponent;